import { useState } from "react";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export function KeywordFlagGuidanceDialog() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        size="icon"
        variant="ghost"
        onClick={() => setOpen(true)}
        data-testid="button-keyword-guidance"
        className="h-6 w-6"
      >
        <Info className="h-3.5 w-3.5" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Keyword Flag Guidance</DialogTitle>
            <DialogDescription>
              Reference for configuring the keyword flag detection list.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm">

            <div className="space-y-1.5">
              <h4 className="font-medium">What this flag does</h4>
              <p className="text-muted-foreground">
                When an interaction note contains one or more terms from the keyword list,
                the interaction is flagged at ingest time for staff review. Matching is
                case-insensitive and whole-word — the term must appear as a standalone word,
                not as part of a longer word. Each flagged interaction records the matched
                terms so you can see exactly what triggered the flag.
              </p>
            </div>

            <div className="space-y-1.5">
              <h4 className="font-medium">Recommended starting terms (48 terms)</h4>
              <p className="text-muted-foreground">
                These terms were confirmed against BOSTON and SALEM interaction notes.
                Note false-positive considerations for higher-risk entries:
              </p>
              <div className="bg-muted/50 rounded-md p-3 font-mono text-xs leading-relaxed">
                lunging, lunge, lunged, snapping, snap, snapped, growling, growl, growled,
                bite, biting, bit her, bit him, bit me, charged, charging, cornered,
                got out, got loose, escaped, muzzle, muzzled, staff only, caution,
                cowering, cower, cowered, trembling, tremble, trembled, panicked, panic,
                over threshold, kennel stress, escalating, escalated, escalation,
                spinning, spun, shut down, bleeding, bled, wound, wounded,
                not eating, limping, limp, limped, resource guarding
              </div>
              <ul className="list-disc pl-4 space-y-1 text-muted-foreground mt-2">
                <li>
                  <span className="font-medium text-foreground">pacing</span> — moderate
                  false-positive risk (sometimes describes normal sniffing/exploring behaviour).
                  Consider holding this term until Cape and Methuen notes have been reviewed,
                  or treat it as a secondary signal only when paired with another keyword.
                </li>
                <li>
                  <span className="font-medium text-foreground">got out</span> — occasionally
                  appears as "got out for a walk". Reviewable per note.
                </li>
                <li>
                  <span className="font-medium text-foreground">growling</span> — context-dependent;
                  playful growling ("he was having loads of fun") appears in a minority of notes.
                  Majority are genuine warning signals.
                </li>
                <li>
                  <span className="font-medium text-foreground">muzzle</span> — ~2–3 of every 19
                  hits are routine sedation or vet procedure notes. Still warrants review in most cases.
                </li>
              </ul>
            </div>

            <div className="space-y-1.5">
              <h4 className="font-medium">Terms we chose not to include</h4>
              <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                <li>
                  <span className="font-medium text-foreground">hold</span> — overwhelming
                  false positives from normal handling language ("held him close", "hold the leash").
                </li>
                <li>
                  <span className="font-medium text-foreground">bit</span> — overwhelming
                  false positives from "a bit", "a little bit". Use <em>bite</em>, <em>biting</em>,
                  <em>bit her</em>, <em>bit him</em>, <em>bit me</em> instead.
                </li>
                <li>
                  <span className="font-medium text-foreground">redirect / redirected</span> — standard
                  positive training technique; flagging it would penalise routine good staff behaviour.
                </li>
                <li>
                  <span className="font-medium text-foreground">scratch / scratching</span> — ambiguous
                  in context; routine for cats during grooming, play, and handling.
                </li>
                <li>
                  <span className="font-medium text-foreground">blood</span> — use <em>bleeding</em>
                  instead. "Blood" includes hits like "bloodshot eyes" (routine observation).
                </li>
                <li>
                  <span className="font-medium text-foreground">flag</span> — system/admin usage,
                  not staff concern language.
                </li>
              </ul>
            </div>

            <div className="space-y-1.5">
              <h4 className="font-medium">Location coverage caveat</h4>
              <p className="text-muted-foreground">
                The initial keyword research was based on BOSTON and SALEM interaction notes only.
                Cape and Methuen staff writing patterns may differ — the recommended list is a
                starting point. After the first few weeks, review the flagged interactions from
                Cape and Methuen and adjust terms that produce too many false positives or miss
                locally common concern language at those sites.
              </p>
            </div>

            <div className="space-y-1.5">
              <h4 className="font-medium">Full research detail</h4>
              <p className="text-muted-foreground">
                Complete frequency data, term rationales, and excluded-term analysis are in{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  agent_reports/R1_KEYWORD_RESEARCH.md
                </code>
                .
              </p>
            </div>

          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
